import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';

export const DAY_NAMES = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const SHORTCUTS = [['Hafta içi', [0, 1, 2, 3, 4]], ['Hafta sonu', [5, 6]], ['Her gün', [0, 1, 2, 3, 4, 5, 6]]];
const EMPTY = { mode: 'weekly', days: [0, 1, 2, 3, 4], start_time: '', end_time: '', date_start: '', date_end: '', location_ids: [], is_tipi: [], is_cesidi: [], message: '', note: '', is_active: true };

const MultiCheck = ({ label, hint, options, value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label>{label} <span className="text-xs font-normal text-slate-400">{hint}</span></Label>
    <div className="grid sm:grid-cols-3 gap-2 p-3 border rounded-lg bg-slate-50 max-h-36 overflow-y-auto" data-testid={testId}>
      {options.length === 0 && <p className="text-xs text-slate-400 col-span-3">Seçenek yok</p>}
      {options.map(([id, name]) => (
        <label key={id} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={value.includes(id)} onCheckedChange={(v) => onChange(v ? [...value, id] : value.filter(x => x !== id))} data-testid={`${testId}-${name}`} />{name}
        </label>
      ))}
    </div>
  </div>
);

export const TimeRuleFormDialog = ({ open, kind, item, locations, workAreas, onClose, onSave }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF({ ...EMPTY, ...(item || {}), start_time: item?.start_time || '', end_time: item?.end_time || '', date_start: item?.date_start || '', date_end: item?.date_end || '' }); }, [open, item]);
  const set = (p) => setF(prev => ({ ...prev, ...p }));
  const areas = useMemo(() => workAreas.filter(a => !f.location_ids.length || f.location_ids.includes(a.location_id)), [workAreas, f.location_ids]);
  const tipiOpts = [...new Set(areas.map(a => a.is_tipi).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr')).map(x => [x, x]);
  const cesidiOpts = [...new Set(areas.filter(a => !f.is_tipi.length || f.is_tipi.includes(a.is_tipi)).map(a => a.is_cesidi).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr')).map(x => [x, x]);
  const verb = kind === 'creation' ? 'talep oluşturulamaz' : 'çalışma yapılamaz';
  const valid = f.mode === 'weekly' ? f.days.length && f.start_time && f.end_time : f.date_start && f.date_end && f.date_end >= f.date_start;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="time-rule-dialog">
        <DialogHeader><DialogTitle>{item?.id ? 'Kuralı Düzenle' : 'Yeni Kural'} · {kind === 'creation' ? 'A) Talep Oluşturma Zamanı' : 'B) Çalışma Zamanı'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex rounded-lg border p-0.5 bg-slate-50 w-fit">
            {[['weekly', 'Gün + saat aralığı'], ['dates', 'Belirli tarih aralığı']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => set({ mode: k })} data-testid={`rule-mode-${k}`}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${f.mode === k ? 'bg-white shadow-sm font-medium' : 'text-slate-500'}`}>{l}</button>
            ))}
          </div>
          {f.mode === 'weekly' ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between"><Label>Günler</Label>
                  <div className="flex gap-2">{SHORTCUTS.map(([l, d]) => <button key={l} type="button" className="text-xs text-blue-600 hover:underline" onClick={() => set({ days: d })} data-testid={`rule-days-${l}`}>{l}</button>)}</div>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="rule-days">
                  {DAY_NAMES.map((d, i) => {
                    const on = f.days.includes(i);
                    return <button key={d} type="button" onClick={() => set({ days: on ? f.days.filter(x => x !== i) : [...f.days, i].sort() })} data-testid={`rule-day-${i}`}
                      className={`w-14 rounded-lg border py-2 text-sm font-medium transition-colors ${on ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>{d}</button>;
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Yasak başlangıç saati</Label><Input type="time" value={f.start_time} onChange={(e) => set({ start_time: e.target.value })} data-testid="rule-start-time" /></div>
                <div className="space-y-1.5"><Label>Yasak bitiş saati</Label><Input type="time" value={f.end_time} onChange={(e) => set({ end_time: e.target.value })} data-testid="rule-end-time" /></div>
              </div>
              <p className="text-xs text-slate-500">Bitiş saati başlangıçtan küçükse gece yarısını aşan aralık sayılır (ör. 18:00–08:00).</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Başlangıç tarihi</Label><Input type="date" value={f.date_start} onChange={(e) => set({ date_start: e.target.value })} data-testid="rule-date-start" /></div>
              <div className="space-y-1.5"><Label>Bitiş tarihi</Label><Input type="date" value={f.date_end} onChange={(e) => set({ date_end: e.target.value })} data-testid="rule-date-end" /></div>
            </div>
          )}
          <MultiCheck label="Lokasyon" hint="(boş = tüm lokasyonlar)" options={locations.map(l => [l.id, l.name])} value={f.location_ids} onChange={(v) => set({ location_ids: v })} testId="rule-locations" />
          <MultiCheck label="İş Tipi" hint="(boş = tüm iş tipleri)" options={tipiOpts} value={f.is_tipi} onChange={(v) => set({ is_tipi: v })} testId="rule-tipi" />
          <MultiCheck label="İş Çeşidi" hint="(boş = tüm iş çeşitleri)" options={cesidiOpts} value={f.is_cesidi} onChange={(v) => set({ is_cesidi: v })} testId="rule-cesidi" />
          <div className="space-y-1.5">
            <Label>Uyarı mesajı <span className="text-xs font-normal text-slate-400">(isteğe bağlı)</span></Label>
            <Input value={f.message} onChange={(e) => set({ message: e.target.value })} placeholder={`Boş bırakılırsa otomatik: "... arası ${verb}."`} data-testid="rule-message" />
          </div>
          <div className="space-y-1.5">
            <Label>İç not <span className="text-xs font-normal text-slate-400">(firma görmez)</span></Label>
            <Input value={f.note} onChange={(e) => set({ note: e.target.value })} placeholder="ör. AVM açık saatleri" data-testid="rule-note" />
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_active} onCheckedChange={(v) => set({ is_active: v })} data-testid="rule-active" />Aktif</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rule-cancel-btn">Vazgeç</Button>
          <Button disabled={!valid} onClick={() => onSave({ ...f, kind })} data-testid="rule-save-btn">Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
